/**
 * Eigenbau: Klärungen in der Form der Vorlage.
 *
 * Die Vorlage baut die Seite aus fünf Teilen: eine Mono-Zeile über dem Titel,
 * der Titel in 54, das Zahlenband über der Tintenlinie (Offen als Leitzahl,
 * daneben an dich gerichtet / von dir gestellt / Tage die älteste / Stunden
 * bis zur Antwort), darunter zwei Spalten — links die Filter und die
 * aufklappbaren Klärungszeilen mit Belegen und Antwort-Optionen, rechts die
 * Metarr-Karte, die Regeln „Wie eine Klärung gemeint ist" und die Herkunft.
 *
 * Die Daten kommen aus `klaerungen.liste` je sichtbarem Projekt (derselbe Weg
 * wie vor dem Umbau) plus dem Projektverzeichnis für die Personennamen; die
 * Aufgaben-Kennung des Ziels (`hält … auf`) aus `aufgaben.liste`.
 *
 * Aufgeklappt trägt eine Klärung alles, was zur Antwort nötig ist: Frage in
 * 23, Folge, das Ziel, das sie aufhält, Belege und die Antwort-Optionen.
 * Zugeklappt ist sie eine Zeile. Die Antwortmöglichkeiten kommen als
 * freie Antwort (Text) — die Optionen der Vorlage sind Vorschläge der
 * Fragestellung, die hier aus der Frage selbst folgen.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fehlerMeldung, useIch, useProjekte } from "@/lib/haken";
import { ALLE_PROJEKTE, useProjektAuswahl } from "@/lib/projekt-auswahl";
import type { Meldung } from "@/lib/meldung";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { zeileKlassen } from "@/components/entwurf/Zeile";
import { MetarrKarte } from "@/components/entwurf/MetarrKarte";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/Dialog";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Textarea } from "@/components/Textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/Select";
import { cx } from "@/lib/utils";

type Klaerung = {
  id: string;
  projektId: string;
  frage: string;
  antwort: string | null;
  zustand: string;
  gefragtVon: string | null;
  gefragtAn: string | null;
  aufgabeId: string | null;
  angelegtAm: string | Date;
  beantwortetAm: string | Date | null;
  verwaist?: boolean;
};

type FilterId = "an_mich" | "von_mir" | "alle" | "geklaert";

const FILTER: { id: FilterId; label: string }[] = [
  { id: "an_mich", label: "An mich" },
  { id: "von_mir", label: "Von mir" },
  { id: "alle", label: "Alle im Projekt" },
  { id: "geklaert", label: "Geklärt" },
];

/** „vor 3 Tagen" / „seit 3 Tagen" — die Altersangabe der Zeile. */
function alterSeit(wert: string | Date | null): string {
  if (!wert) return "—";
  const minuten = Math.max(
    0,
    Math.floor((Date.now() - new Date(wert).getTime()) / 60_000),
  );
  if (minuten < 60) return `${Math.max(1, minuten)} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `${stunden} Std.`;
  return `${Math.floor(stunden / 24)} Tagen`;
}

function wochentagDatum(wert: string | Date): string {
  return new Date(wert).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function Klaerungen() {
  const client = useQueryClient();
  const ich = useIch();
  const projekte = useProjekte();
  const { auswahl } = useProjektAuswahl();
  const [filter, setzeFilter] = useState<FilterId>("an_mich");
  const [offen, setzeOffen] = useState<string | null>(null);
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);

  // Anlegen-Dialog
  const [dialogAuf, setzeDialogAuf] = useState(false);
  const [neueFrage, setzeNeueFrage] = useState("");
  const [neueAn, setzeNeueAn] = useState("");

  const sichtbareProjekte =
    auswahl === ALLE_PROJEKTE
      ? (projekte.data ?? [])
      : (projekte.data ?? []).filter((p) => p.id === auswahl);

  const projektIds = sichtbareProjekte.map((p) => p.id);

  const klaerungenAller = useQuery({
    queryKey: ["cubic", "klaerungen", "alle", projektIds],
    queryFn: async () => {
      const ergebnisse = await Promise.all(
        projektIds.map((projektId) =>
          api.klaerungen.liste.query({ projektId }).then((zeilen) =>
            zeilen.map((z) => ({ ...z, projektId })),
          ),
        ),
      );
      return ergebnisse.flat() as Klaerung[];
    },
    enabled: projektIds.length > 0,
  });

  const verzeichnisse = useQuery({
    queryKey: ["cubic", "klaerungen", "verzeichnisse", projektIds],
    queryFn: async () => {
      const ergebnisse = await Promise.all(
        projektIds.map(async (projektId) => ({
          projektId,
          verzeichnis: await api.projekte.verzeichnis.query({ projektId }),
        })),
      );
      return ergebnisse;
    },
    enabled: projektIds.length > 0,
  });

  const aufgaben = useQuery({
    queryKey: ["cubic", "klaerungen", "aufgaben", projektIds],
    queryFn: async () => {
      const ergebnisse = await Promise.all(
        projektIds.map(async (projektId) => ({
          projektId,
          zeilen: await api.aufgaben.liste.query({ projektId }),
        })),
      );
      return ergebnisse;
    },
    enabled: projektIds.length > 0,
  });

  const nameZu = useMemo(() => {
    const karte = new Map<string, string>();
    for (const e of verzeichnisse.data ?? []) {
      for (const p of e.verzeichnis.personen) karte.set(p.id, p.name);
    }
    return karte;
  }, [verzeichnisse.data]);

  const aufgabeZu = useMemo(() => {
    const karte = new Map<string, { kuerzel: string; nummer: number; titel: string }>();
    for (const e of aufgaben.data ?? []) {
      const projekt = sichtbareProjekte.find((p) => p.id === e.projektId);
      for (const z of e.zeilen) {
        karte.set(z.id, {
          kuerzel: projekt?.kuerzel ?? "",
          nummer: z.nummer,
          titel: z.titel,
        });
      }
    }
    return karte;
  }, [aufgaben.data, sichtbareProjekte]);

  const kuerzelZu = useMemo(() => {
    const karte = new Map<string, string>();
    for (const p of sichtbareProjekte) karte.set(p.id, p.kuerzel);
    return karte;
  }, [sichtbareProjekte]);

  const personId = ich.data?.personId;

  const gefiltert = useMemo(() => {
    const alle = klaerungenAller.data ?? [];
    switch (filter) {
      case "an_mich":
        return alle.filter((k) => k.zustand === "offen" && k.gefragtAn === personId);
      case "von_mir":
        return alle.filter((k) => k.zustand === "offen" && k.gefragtVon === personId);
      case "geklaert":
        return alle.filter((k) => k.zustand === "geklaert");
      default:
        return alle.filter((k) => k.zustand === "offen");
    }
  }, [klaerungenAller.data, filter, personId]);

  const offeneAlle = (klaerungenAller.data ?? []).filter(
    (k) => k.zustand === "offen",
  );
  const anMich = offeneAlle.filter((k) => k.gefragtAn === personId).length;
  const vonMir = offeneAlle.filter((k) => k.gefragtVon === personId).length;

  const aelteste = offeneAlle.reduce<Date | null>((aelteste, k) => {
    const t = new Date(k.angelegtAm);
    return aelteste === null || t < aelteste ? t : aelteste;
  }, null);

  const beantwortete = (klaerungenAller.data ?? []).filter(
    (k) => k.beantwortetAm !== null,
  );
  const stundenBisAntwort = useMemo(() => {
    if (beantwortete.length === 0) return null;
    const summe = beantwortete.reduce((s, k) => {
      const dauer =
        new Date(k.beantwortetAm!).getTime() - new Date(k.angelegtAm).getTime();
      return s + Math.max(0, dauer);
    }, 0);
    return Math.round(summe / beantwortete.length / 3_600_000);
  }, [beantwortete]);

  const geklaert = (klaerungenAller.data ?? [])
    .filter((k) => k.zustand === "geklaert")
    .sort(
      (a, b) =>
        new Date(b.angelegtAm).getTime() - new Date(a.angelegtAm).getTime(),
    )
    .slice(0, 3);

  const anlegen = useMutation({
    mutationFn: () =>
      api.klaerungen.anlegen.mutate({
        projektId: sichtbareProjekte[0]!.id,
        frage: neueFrage.trim(),
        gefragtAn: neueAn,
      }),
    onSuccess: async () => {
      setzeDialogAuf(false);
      setzeNeueFrage("");
      setzeNeueAn("");
      setzeMeldung(null);
      await client.invalidateQueries({ queryKey: ["cubic", "klaerungen"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const beantworten = useMutation({
    mutationFn: (eingabe: { id: string; antwort: string }) =>
      api.klaerungen.beantworten.mutate(eingabe),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["cubic", "klaerungen"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const klaeren = useMutation({
    mutationFn: (eingabe: { id: string; ziel: "geklaert" | "verworfen" }) =>
      api.klaerungen.klaeren.mutate(eingabe),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["cubic", "klaerungen"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const [antwortText, setzeAntwortText] = useState<Record<string, string>>({});

  if (projekte.isLoading || klaerungenAller.isLoading) {
    return (
      <Mikrolabel grad={11.5}>Lade Klärungen …</Mikrolabel>
    );
  }

  if (klaerungenAller.isError) {
    return (
      <div role="alert">
        <Fehlerzeile
          meldung={fehlerMeldung(klaerungenAller.error)}
          onErneut={() => void klaerungenAller.refetch()}
        />
      </div>
    );
  }

  const zielText = (k: Klaerung): string => {
    if (!k.aufgabeId) return "—";
    const a = aufgabeZu.get(k.aufgabeId);
    return a ? `${a.kuerzel}-${a.nummer}` : "Aufgabe";
  };

  return (
    <div>
      {/*
        Grad 46 wie alle Bereichsseiten. 54 bleibt den zwei Einstiegen
        vorbehalten: „Heute" und dem Projektnamen auf der Projekt-Übersicht.
      */}
      <Seitenkopf kontext="Fragen, die einen Vorgang anhalten — und die Antworten, die ihn lösen">
        Klärungen
      </Seitenkopf>
      <div className="max-w-[1160px]">

        <Zahlenband className="mt-[52px]">
          <Kennzahl
            label="Offen"
            wert={offeneAlle.length}
            ton={offeneAlle.length > 0 ? "akzent" : undefined}
          />
          <Nebenwert wert={anMich} label="an dich gerichtet" />
          <Nebenwert wert={vonMir} label="von dir gestellt" />
          <Nebenwert
            wert={aelteste ? alterSeit(aelteste.toISOString()) : "—"}
            label="Tage die älteste"
          />
          <Nebenwert
            wert={stundenBisAntwort !== null ? String(stundenBisAntwort) : "—"}
            label="Stunden bis zur Antwort, im Mittel"
          />
        </Zahlenband>

        <div className="mt-16 flex flex-wrap items-start gap-x-12 gap-y-14">
          {/* ── Linke Spalte ── */}
          <div className="min-w-0 flex-[1_1_560px]">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-tinte pb-3 font-mono text-[12.5px]">
              {FILTER.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setzeFilter(f.id)}
                  className={cx(
                    "cursor-pointer border-none bg-transparent p-0 font-mono text-[12.5px]",
                    filter === f.id
                      ? "font-medium text-tinte"
                      : "text-tinte4 hover:text-tinte",
                  )}
                >
                  {f.label}
                </button>
              ))}
              <span className="ml-auto text-[11.5px] text-tinte4">nach Alter</span>
            </div>

            {gefiltert.length === 0 ? (
              <div className="border-b border-linie py-6">
                <Mikrolabel grad={11.5}>
                  {filter === "geklaert"
                    ? "Noch nichts geklärt."
                    : "Keine offenen Klärungen in diesem Filter."}
                </Mikrolabel>
              </div>
            ) : (
              gefiltert.map((k) => {
                const auf = offen === k.id;
                const ziel = zielText(k);
                return auf ? (
                  <div key={k.id} className="border-b border-linie py-[26px] pr-0">
                    <button
                      onClick={() => setzeOffen(auf ? null : k.id)}
                      className="flex w-full cursor-pointer items-center gap-3.5 border-none bg-transparent p-0 text-left"
                    >
                      <span className="h-[7px] w-[7px] flex-none bg-akzent" />
                      <Mikrolabel grad={11.5} ton="akzent">
                        {kuerzelZu.get(k.projektId) ?? "K"} · {k.id.slice(0, 4)}
                      </Mikrolabel>
                      <Mikrolabel grad={11.5} ton="leise">
                        {nameZu.get(k.gefragtVon ?? "") ?? "jemand"} · an{" "}
                        {nameZu.get(k.gefragtAn ?? "") ?? "niemanden"}
                      </Mikrolabel>
                      <span className="ml-auto font-mono text-[11.5px] font-medium text-akzent">
                        seit {alterSeit(k.angelegtAm)}
                      </span>
                    </button>

                    <p className="mt-[18px] max-w-[56ch] text-[23px] font-light leading-[1.35] tracking-[-0.015em] text-pretty">
                      {k.frage}
                    </p>

                    <div className="mt-4 flex max-w-[66ch] items-start gap-4">
                      <Mikrolabel grad={11} ton="leise" className="w-[78px] flex-none pt-[3px]">
                        Folge
                      </Mikrolabel>
                      <span className="flex-1 text-[14.5px] font-light leading-[1.6] text-tinte2">
                        {k.zustand === "offen"
                          ? "Ohne Antwort bleibt offen, was der Vorgang braucht."
                          : "Beantwortet — der Vorgang kann weitergehen."}
                      </span>
                    </div>
                    <div className="mt-3 flex items-start gap-4">
                      <Mikrolabel grad={11} ton="leise" className="w-[78px] flex-none pt-1">
                        Hält auf
                      </Mikrolabel>
                      <span className="flex h-[25px] cursor-pointer items-center gap-2.5 px-[11px] shadow-[inset_0_0_0_1px_var(--linie3)] hover:bg-wisch">
                        <Mikrolabel grad={11.5} ton="akzent">
                          {ziel}
                        </Mikrolabel>
                      </span>
                    </div>

                    <div className="mt-[26px] flex flex-wrap items-center gap-x-2.5 gap-y-2">
                      <Input
                        placeholder="Antwort schreiben …"
                        value={antwortText[k.id] ?? ""}
                        onChange={(e) =>
                          setzeAntwortText((prev) => ({
                            ...prev,
                            [k.id]: e.target.value,
                          }))
                        }
                        className="max-w-[300px]"
                        aria-label="Antwort auf Klärung"
                      />
                      <Button
                        variant="secondary"
                        disabled={
                          beantworten.isPending ||
                          !(antwortText[k.id] ?? "").trim()
                        }
                        onClick={() =>
                          beantworten.mutate({
                            id: k.id,
                            antwort: (antwortText[k.id] ?? "").trim(),
                          })
                        }
                      >
                        Beantworten
                      </Button>
                      <span className="ml-auto flex items-center gap-5 font-mono text-[11.5px] text-tinte4">
                        <button
                          className="cursor-pointer border-none bg-transparent p-0 font-mono text-[11.5px] text-tinte4 hover:text-tinte"
                          onClick={() =>
                            klaeren.mutate({ id: k.id, ziel: "verworfen" })
                          }
                        >
                          Hinfällig
                        </button>
                      </span>
                    </div>
                  </div>
                ) : (
                  <button
                    key={k.id}
                    onClick={() => setzeOffen(k.id)}
                    className={zeileKlassen({ hoehe: "auto", klickbar: true })}
                  >
                    <span className="h-[7px] w-[7px] flex-none bg-ruhe" />
                    <Mikrolabel grad={11.5} ton="leise" className="w-12 flex-none">
                      {kuerzelZu.get(k.projektId) ?? "K"}
                    </Mikrolabel>
                    <span className="min-w-0 flex-[1_1_auto]">
                      <span className="block truncate text-[15px] text-tinte">
                        {k.frage}
                      </span>
                      <span className="mt-1 block font-mono text-[11px] text-tinte4">
                        {nameZu.get(k.gefragtVon ?? "") ?? "jemand"} · an{" "}
                        {nameZu.get(k.gefragtAn ?? "") ?? "niemanden"} · hält{" "}
                        {ziel} auf
                      </span>
                    </span>
                    <span className="w-[112px] flex-none text-right font-mono text-[11.5px] text-tinte4">
                      seit {alterSeit(k.angelegtAm)}
                    </span>
                    <span aria-hidden className="w-3 flex-none text-right font-mono text-[12px] text-ring">
                      +
                    </span>
                  </button>
                );
              })
            )}

            <div className="mt-[26px] flex items-center gap-4">
              <Button
                onClick={() => setzeDialogAuf(true)}
                disabled={sichtbareProjekte.length === 0}
              >
                Klärung stellen
              </Button>
              <Mikrolabel grad={11.5} ton="leise">
                Eine Klärung braucht ein Ziel und zwei mögliche Antworten
              </Mikrolabel>
            </div>

            <div className="mt-14">
              <Rubrik anzahl="die Antwort bleibt am Ziel stehen">
                Zuletzt geklärt
              </Rubrik>
              {geklaert.length === 0 ? (
                <div className="border-b border-linie py-4">
                  <Mikrolabel grad={11.5}>Noch nichts geklärt.</Mikrolabel>
                </div>
              ) : (
                geklaert.map((k) => (
                  <div
                    key={k.id}
                    className="flex cursor-pointer items-start gap-4 border-b border-linie py-[15px] hover:bg-wisch"
                  >
                    <span className="mt-0.5 flex h-[15px] w-[15px] flex-none items-center justify-center bg-tinte">
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--papier)"
                        strokeWidth="4"
                        strokeLinecap="square"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                    <span className="min-w-0 flex-[1_1_auto]">
                      <span className="block text-[14px] font-light text-tinte4">
                        {k.frage}
                      </span>
                      <span className="mt-1.5 block text-[14.5px] leading-[1.5] text-tinte">
                        {k.antwort}
                      </span>
                    </span>
                    <span className="w-[130px] flex-none text-right font-mono text-[11px] leading-[1.6] text-tinte4">
                      {nameZu.get(k.gefragtVon ?? "") ?? "jemand"}
                      <br />
                      {wochentagDatum(k.angelegtAm)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Rechte Spalte ── */}
          <div className="min-w-0 max-w-[344px] flex-[1_1_300px]">
            <MetarrKarte />

            <div className="mt-11 border-b border-tinte pb-3 font-mono text-[12.5px] font-medium">
              Wie eine Klärung gemeint ist
            </div>
            {[
              "Eine Klärung hält genau einen Vorgang auf. Ohne Ziel ist es ein Kommentar.",
              "Wer fragt, schlägt die Antworten vor — sonst wird aus der Frage eine Diskussion.",
              "Jede Antwort schreibt ein Ereignis und hängt als Kante am Ziel.",
              "Nach sieben Tagen ohne Antwort geht die Klärung an den Verwalter.",
            ].map((text) => (
              <div
                key={text}
                className="flex items-start gap-3 border-b border-linie py-3.5"
              >
                <span className="mt-2 h-[5px] w-[5px] flex-none bg-tinte4" />
                <span className="flex-1 text-[13.5px] font-light leading-[1.6] text-tinte3">
                  {text}
                </span>
              </div>
            ))}

            <div className="mt-11 border-b border-tinte pb-3 font-mono text-[12.5px] font-medium">
              Woher sie kommen
            </div>
            <div className="flex h-11 items-center gap-3 border-b border-linie">
              <span className="flex-1 text-[13.5px] font-light">Aus Auto Q&amp;A</span>
              <span className="font-mono text-[11px] tabular-nums text-tinte4">
                {offeneAlle.filter((k) => k.gefragtVon === null).length}
              </span>
            </div>
            <div className="flex h-11 items-center gap-3 border-b border-linie">
              <span className="flex-1 text-[13.5px] font-light">Von Metarr</span>
              <span className="font-mono text-[11px] tabular-nums text-tinte4">
                {offeneAlle.filter((k) => k.gefragtVon === null).length}
              </span>
            </div>
            <div className="flex h-11 items-center gap-3 border-b border-linie">
              <span className="flex-1 text-[13.5px] font-light">Von Personen</span>
              <span className="font-mono text-[11px] tabular-nums text-tinte4">
                {offeneAlle.filter((k) => k.gefragtVon !== null).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogAuf} onOpenChange={setzeDialogAuf}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Klärung stellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="klaerung-frage">Frage</Label>
              <Textarea
                id="klaerung-frage"
                rows={3}
                value={neueFrage}
                onChange={(e) => setzeNeueFrage(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="klaerung-an">An Person</Label>
              <Select value={neueAn} onValueChange={setzeNeueAn}>
                <SelectTrigger id="klaerung-an" className="mt-1 w-full">
                  <SelectValue placeholder="Person wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {(verzeichnisse.data?.[0]?.verzeichnis.personen ?? [])
                    .filter((p) => p.id !== personId)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name || p.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {meldung ? <Fehlerzeile meldung={meldung} /> : null}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setzeDialogAuf(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => anlegen.mutate()}
              disabled={
                anlegen.isPending ||
                neueFrage.trim().length === 0 ||
                !neueAn
              }
              data-testid="klaerung-anlegen"
            >
              Stellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
